import React from 'react';
import styled from "@emotion/styled";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from "@mui/styles";
import CircleIcon from '@mui/icons-material/Circle';

const DistributionPieCard = ({ title, pieChartData }) => {
  const theme = useTheme()

  const COLORS = [`${theme.closure.refinance.pieChart}`, `${theme.closure.dismissal.pieChart}`];
  
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = outerRadius + 8;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    return (
      <text x={x} y={y} fill={theme.palette.common.black} fontSize={11} fontWeight={850} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const noClosedActivity = pieChartData[0].size === 0 && pieChartData[1].size === 0

  return (
    <DistributionCard>
      <CardTitle>{title}</CardTitle>
      <ChartFrame>
        {noClosedActivity ?
          <EmptyState>none closed</EmptyState>
        :
          <ResponsiveContainer width="100%" height="100%" minHeight={82}>
            <PieChart width={400} height={300}>
              <Pie
                label={renderCustomizedLabel}
                dataKey="size"
                startAngle={180}
                endAngle={0}
                data={pieChartData}
                cx="50%"
                cy="82%"
                outerRadius="76%"
                innerRadius="26%"
                fill="#8884d8"
                labelLine={false}
                animationDuration={500}
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} stroke={theme.palette.common.white} strokeWidth={2} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip title={title} />} cursor={{fill: `rgba(147, 149, 153, 0.1)`}} />
            </PieChart>
          </ResponsiveContainer>
        }
      </ChartFrame>
      <LegendRows>
        <LegendRow>
          <LegendLabel>
            <CircleIcon style={{color: `${theme.closure.refinance.pieChart}`}} />
            <span>Closed</span>
          </LegendLabel>
          <LegendValue>
            {pieChartData !== undefined ?
              pieChartData[0].size
            :
              <></>
            }
          </LegendValue>
        </LegendRow>
        <LegendRow>
          <LegendLabel>
            <CircleIcon style={{color: `${theme.closure.dismissal.pieChart}`}} />
            <span>Dismissed</span>
          </LegendLabel>
          <LegendValue>
            {pieChartData !== undefined ?
              pieChartData[1].size
            :
              <></>
            }
          </LegendValue>
        </LegendRow>
      </LegendRows>
    </DistributionCard>
  )
}

const CustomTooltip = ({ active, payload, title }) => {
  if (active && payload && payload.length) {
    return (
      <TooltipSurface>
        <TooltipHeader>{title}</TooltipHeader>
        <TooltipRow>
          <TooltipLabel>{payload[0].name || payload[0].payload?.name}</TooltipLabel>
          <TooltipValue>{payload[0].value}</TooltipValue>
        </TooltipRow>
      </TooltipSurface>
    )
  }

  return null
}

const DistributionCard = styled.article(({ theme }) => `
  min-width: 0;
  display: grid;
  min-height: 0;
  grid-template-rows: auto minmax(5rem, 1fr) auto;
  gap: 0.28rem;
  padding: 0.42rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.62rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.16rem 0.5rem rgba(15, 23, 42, 0.035);
`)

const CardTitle = styled.h3(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 850;
  line-height: 1.2;
  text-align: center;
`)

const ChartFrame = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 5rem;
  display: grid;
  place-items: center;
  border: 0.1rem solid ${theme.palette.common.grayBorderLight};
  border-radius: 0.52rem;
  background: ${theme.palette.common.white};
  overflow: hidden;
`)

const EmptyState = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.74rem;
  font-weight: 800;
  line-height: 1.2;
`)

const LegendRows = styled.div(`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(max-content, 1.18fr);
  gap: 0.24rem;
`)

const LegendRow = styled.div(({ theme }) => `
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.22rem;
  align-items: center;
  padding: 0.24rem 0.3rem;
  border: 0.1rem solid ${theme.palette.common.grayBorderLight};
  border-radius: 0.45rem;
  background: ${theme.palette.common.white};
`)

const LegendLabel = styled.div(({ theme }) => `
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.62rem;
  font-weight: 750;
  line-height: 1.2;
  overflow: hidden;
  white-space: nowrap;

  svg {
    width: 0.58rem;
    height: 0.58rem;
    flex: 0 0 auto;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`)

const LegendValue = styled.div(({ theme }) => `
  min-width: max-content;
  color: ${theme.palette.common.black};
  font-size: 0.68rem;
  font-weight: 850;
  line-height: 1;
  text-align: right;
`)

const TooltipSurface = styled.div(({ theme }) => `
  width: 10.5rem;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.62rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.7rem 1.4rem rgba(15, 23, 42, 0.14);
`)

const TooltipHeader = styled.div(({ theme }) => `
  padding: 0.5rem 0.6rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 850;
  line-height: 1.2;
`)

const TooltipRow = styled.div(`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
`)

const TooltipLabel = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.72rem;
  font-weight: 750;
  line-height: 1.2;
`)

const TooltipValue = styled.span(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 850;
  line-height: 1.2;
`)

export default DistributionPieCard
